import React from 'react';
import PropTypes from 'prop-types';
import {DragSource, DropTarget} from 'react-dnd';
import compose from 'recompose/compose';
import {Radio} from 'semantic-ui-react';
import {FormattedMessage} from 'react-intl';
import formContextShape from './ResourceFormContextShape';
import {isNoU, TopLevelSectionName} from "utils/DBUtils";
import {matchBy} from "../../utils/collectionManagmentUtils";

const collectionDropTarget = {
    // elements within a collection can only swap positions with each other; they cannot
    // be dropped on the collection component itself.
    drop() {
    }
};

const elementDragSrouce = {
    beginDrag: (props, monitor) => ({
      ...props
    }),
    endDrag: (props, monitor) => {
        const item = monitor.getItem();
        // console.log(monitor);
        const result = monitor.getDropResult();
        // console.log('dragged', item);
        // console.log('dropped', result);

        if (!isNoU(item) && !isNoU(result)) {
            const draggedId = item.id;
            const droppedId = result.id;

            if (monitor.didDrop() && draggedId !== droppedId) {
                props.moveElement(item.currentIndex, result.currentIndex);
            }
        }
    }
};

const elementDropTarget = {
    drop: (props, monitor) => ({
      ...props
    })
};

const deleteCount = (collection) => collection
    .map(item => item.deleted ? 1 : 0)
    .reduce((result, val) => result + val, 0);

const makeIdOrder = (collection) => collection
    .filter(item => !item.deleted)
    .reduce((map, item) => {
        map[item.id] = item.displayOrder;
        return map;
    }, {});

// const isStep = element => includes(AvailableSteps, get(element, 'resourceId.type', null));

class CollectionManagementSection extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            sortable: false,
            elements: this.makeElements()
        };
    }

    // needed while in the initial call to makeElements(), before the initial state gets set in the constructor.
    state = {
        sortable: false,
    };

    // TODO: See if we can move this into an HOC
    componentWillReceiveProps(newProps) {
        // Should only regenerate elements if the collection has changed,
        // changed is based on order of items in collection by id

        if (newProps.fieldPath && newProps.fieldPath && (newProps.fieldPath[newProps.fieldPath -1] === "dropdownButton")) {
            console.log("dropdownButton")
        }

        let currentDeleteCount = this.props.collection && this.props.collection.length > 0 ? deleteCount(this.props.collection) : 0;
        let newDeleteCount = newProps.collection && newProps.collection.length > 0 ? deleteCount(newProps.collection) : 0;

        // Just doing a delete count check for now to signal item removal
        // Probably can be optimized to remove just the element(s) that were deleted

        if (newProps.collection.length === this.props.collection.length
            && this.props.collection.length > 0
            && currentDeleteCount === newDeleteCount) {
            // lengths are the same

            // The case of reordering steps in step management
            // this collection needs to be updated to represent same order
            // create maps of the items' id to its displayOrder
            let currentIdOrders = makeIdOrder(this.props.collection);
            let newIdOrders = makeIdOrder(newProps.collection);
            let keys = Object.keys(currentIdOrders);

            // Compare each id, if the display orders are different then a reorder has occurred
            let hasBeenReordered = false;
            for (let key of keys) {
                if (currentIdOrders[key] !== newIdOrders[key]) {
                    hasBeenReordered = true;
                }
            }

            // Currently, only case of a reorder cause outside of this component is steps
            // because of how sections are made, they are bound to a specific index,
            // we need to recreate each section
            if (hasBeenReordered) {
                const newCollection = this.processElements(newProps.collection);

                const newElements = newCollection.map(({resource, fieldPath, resourcePath}, i) => {
                  // Custom diff to prevent recreating each element section
                  const componentExists = this.props.collection.find(el => el.id === resource.id);

                  if (componentExists) {
                    // exists, create new existing components, disable animation to prevent flicker
                    // TODO: Look in to better way
                    // When reordering steps, we need to recreate the component to have the proper step config linked
                    return React.cloneElement(this.makeElement(fieldPath, resourcePath, i, false, resource), {animate: false});
                  } else {
                    // doesn't exist, need to create new component
                    return this.makeElement(fieldPath, resourcePath, i, true, resource);
                  }
                });

                this.setState({
                  elements: newElements
                });

                return;
            }
        }

        const newCollection = this.processElements(newProps.collection);

        const newElements = newCollection.map(({resource, fieldPath, resourcePath}, i) => {
            // If the current item is same as the old item in the same index
            // Then we clone the element, otherwise we need to recreated the item
            const existing = this.props.collection[i];
            let exists = false;
            if (existing && existing.id === resource.id) {
              exists = true;
            }

            if (exists) {
                // exists, create new existing components, disable animation to prevent flicker
                let existingElement = this.state.elements[i];
                return React.cloneElement(existingElement, {animate: false})
            } else {
                // doesn't exist, need to create new component
                try {
                    return this.makeElement(fieldPath, resourcePath, i, true, resource);
                } catch (e) {
                    console.log("unable to makeElement", e)
                }
            }
        });

        this.setState({
            elements: newElements
        });
    }

    makeElementPaths = (fieldPath, resourcePath, i) => {
        // Make new field path where the array name and the current index are combined as a path part
        let elementPath = fieldPath.slice();
        const collectionPath = elementPath[elementPath.length - 1];
        elementPath[elementPath.length - 1] = collectionPath + `.${i}`;

        let elementResourcePath = resourcePath.slice();

        if (this.props.field.resource) {
            // Make new resource path with the array index as well
            const resourceCollectionPath = elementResourcePath[elementPath.length - 1];
            elementResourcePath[elementResourcePath.length - 1] = resourceCollectionPath + `.${i}`;
        }

        return {
            fieldPath: elementPath,
            resourcePath: elementResourcePath
        };
    };

    // returns a list that looks like [{resource, fieldPath, resourcePath}] and includes neither
    // deletions nor resources missing ids.
    processElements(collection) {
        const {fieldPath, resourcePath} = this.props.formProxy;

        if (!Array.isArray(collection)) {
            console.warn("collection is not an array!")
        }

        // 2025. I don't know how we get here, but in some cases, adding an item returns a single item
        // so we wrap it if we need to 
        let newCollection = (Array.isArray(collection) ? collection : [collection])
            .filter(element => {
                if (isNoU(element.id)) {
                    console.error("No id for element, cannot compare", element);
                    return false;
                }
                return true;
            })
            .map((resource, i) => {
                // creating elements' new field- and resourcePaths must occur before filtering out
                // deletions, so as to use the element's true resource index when creating those
                // paths, and also to to ensure that section headers, etc. will use the array index.
                const elementPaths = this.makeElementPaths(fieldPath, resourcePath, i);
                return {resource, ...elementPaths};
            })
            .filter(({resource}) => !resource.deleted);

        let behavior = this.props.field.behavior;
        if (!isNoU(behavior)) {
            switch (behavior.type) {
                case 'matchBy':
                    newCollection = matchBy(behavior, newCollection);
                    break;
            }
        } else {
            // Default behavior
            // Sort by displayOrder
            newCollection.sort((item1, item2) => {
                let item1Order = item1.resource.displayOrder || -1;
                let item2Order = item2.resource.displayOrder || -1;
                return item1Order - item2Order;
            });
        }

        return newCollection;
    }

    makeElements() {
        const newCollection = this.processElements(this.props.collection);

        const newElements = newCollection.map(({resource, fieldPath, resourcePath}, i) => {
            return this.makeElement(fieldPath, resourcePath, i, false, resource);
        });

        return newElements;
    }

    findElement = id => {
        return this.props.collection.findIndex(e => e.id === id);
    };

    moveElement = (fromIndex, toIndex) => {
        // console.log("pre", this.props.collection.slice());
        const reordered = this.props.collection.slice();

        // console.log(`${fromIndex} -> ${toIndex}`);

        // Remove item we're moving
        let removed = reordered.splice(fromIndex, 1)[0];

        // Insert that item to new spot
        reordered.splice(toIndex, 0, removed);

        // console.log("post", reordered.slice());
        let newOrder = reordered.map((item) => item.displayOrder);
        this.props.saveDisplayOrder({newOrder, orderField: this.props.field.sortableName});
    };

    toggleSortability = (event, data) => {
        // TODO: Look into way to always have children be sortable elements and toggle there drag-ability instead
        if (data.checked) {
          // Set to sortable and make separate list for sorting
          const processed = this.processElements(this.props.collection);
          const sortElements = processed.map(({resource, fieldPath, resourcePath}, i) => {
              return this.makeElementSortable(React.cloneElement(this.state.elements[i]), i, resource);
          });

          this.setState({
            sortElements: sortElements,
            sortable: true
          });
        } else  {
            // Unset sortable
            this.setState({
              sortElements: null,
              sortable: false
            });
        }
    };

    makeElementSortable(element, i, resource) {
        const SortableComponent = props => props.connectDropTarget(props.connectDragSource(<div>{element}</div>));

        const ConnectedSortableComponent = compose(
            DropTarget(props => props.type, elementDropTarget, connect => ({
                connectDropTarget: connect.dropTarget()
            })),
            DragSource(props => props.type, elementDragSrouce, (connect, monitor) => ({
                connectDragSource: connect.dragSource(),
                isDragging: monitor.isDragging()
            }))
        )(SortableComponent);

        const sortableElement = (
            <ConnectedSortableComponent
                key={resource.resourceId.id}
                id={resource.resourceId.id}
                type={this.props.section.resourceType || resource.resourceId.type}
                findElement={this.findElement}
                moveElement={this.moveElement}/>
        );

        return sortableElement;
    }

    makeElement(elementPath, elementResourcePath, i, animate, resource) {
        let element = null;

        // Need to handle section creation differently for top level sections, since that is how the original implementation worked
        // 'makeSectionWrapper' was implemented to create a top level section
        // and 'makeSectionFieldsWithWrapper' was implemented to create sub sections

        // 'makeSectionFieldsWithWrapper' ends up calling 'makeSectionWrapper',
        // however 'makeSectionFieldsWithWrapper' grabs the wrapper component from the field instead of using an argument
        // 'makeSectionFieldsWithWrapper' also doesn't take in a sectionGroup which is currently only used by top level accordions

        // TODO: Make topLevel creation same as sub section creation
        if (this.props.topLevel) {
            const elementProxy = this.props.formProxy.makeProxy(elementPath, elementResourcePath);

            const key = this.props.field.getUniqueSectionKey
                ? this.props.field.getUniqueSectionKey(elementProxy)
                : ((this.props.field.key || "") + (this.props.field.name || ""));

            // const key = this.props.field.key || elementPath

            element = this.props.formProxy.makeSectionWrapper(
                this.props.formProxy.sectionWrapper(),
                key,
                this.props.field,
                this.props.sectionIndex + i,
                elementPath,
                elementResourcePath,
                this.props.formProxy.makeSectionFields(this.props.field, elementPath, elementResourcePath, this.props.sectionIndex + i, [key]),
                TopLevelSectionName
            );
        } else {
            let section = this.props.formProxy.makeSectionFieldsWithWrapper(this.props.field, elementPath, elementResourcePath, i, this.props.sectionKeyPath);
            element = React.cloneElement(section, {
                    animate: animate
                }
            );
        }

        return this.state.sortable ? this.makeElementSortable(element, i, resource) : element;
    }

    render() {
        return this.props.field.sortable ? (
            <React.Fragment>
                <div className="toggle-sort-radio">
                    {this.state.elements.length > 1 ? (
                        <React.Fragment>
                            <Radio toggle className="db-lightblue-radio" onChange={this.toggleSortability}/>
                            {this.props.field.toggleSortLabelKey
                                ? <FormattedMessage id={this.props.field.toggleSortLabelKey}/>
                                : <span>{this.props.field.toggleSortLabel}</span>}
                        </React.Fragment>
                    ) : ""}
                </div>
                {this.state.sortable
                  ? this.props.connectDropTarget(
                    <div className="sortable-collection">
                      {this.state.sortElements.map((ele, i) => React.cloneElement(ele, {currentIndex: i}))}
                    </div>
                  )
                  : this.state.elements}
            </React.Fragment>
        ) : this.state.elements;
    }
}

CollectionManagementSection.propTypes = {
    formProxy: formContextShape,
    collection: PropTypes.array,
    sectionKeyPath: PropTypes.arrayOf(PropTypes.string),
    topLevel: PropTypes.bool
};

export default DropTarget(props => props.field.resourceType || '', collectionDropTarget, connect => ({
    connectDropTarget: connect.dropTarget()
}))(CollectionManagementSection);



// WEBPACK FOOTER //
// ./public/components/forms/CollectionManagementSection.jsx